import { useEffect, useState, useCallback } from 'react';
import {Dialog, DialogTitle, DialogContent, Typography, Box,Button, DialogActions, Pagination, TextField, Grid, Divider, Chip} from '@mui/material';
import AddStudentDialog from './AddStudentDialog';
import { useFetch } from '../../utils/FetchTemplate';
import { useSetting } from '../../contexts/SettingContext';
import { IMember } from '../../interfaces/Member.interface';
import { IService } from '../../interfaces/Setting.interface';
import { IPayment } from '../../interfaces/StudentPayment.interface';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { Role, ServiceType } from '../../interfaces/constants';

import { useBookings } from '../../contexts/BookingContext';

interface ServiceDetailDialogProps {
  open: boolean;
  service: any;
  onClose: () => void;
}

const ServiceDetailDialog: React.FC<ServiceDetailDialogProps> = ({ open, service, onClose }) => {
  const [openAddStudent, setOpenAddStudent] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [InitialData, setInitialData] = useState<{
    member: IMember;
    payment: IPayment;
  } | undefined>(undefined);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [targetStudentId, setTargetStudentId] = useState<string | null>(null);

  const { typedPostData, postData } = useFetch();
  const { settings } = useSetting();
  
  // Check if current user is Admin
  const currentUserRole = settings?.profile?.role;
  const isAdmin = currentUserRole === Role.Admin;

  const [students, setStudents] = useState<IMember[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStudents, setFilteredStudents] = useState<IMember[]>([]);

  const [page, setPage] = useState(1);
  const studentsPerPage = 5;

  const { fetchBookings } = useBookings();
  const { fetchSettings } = useSetting();

  const fetchStudentDetails = useCallback(async (serviceId: string) => {
    if (!serviceId || !settings.userId) return;
    try {
      const latestService = await typedPostData<{ userId: string; serviceId: string }, IService>(
        '/api/setting/getServiceById',
        { userId: settings.userId, serviceId }
      );

      const memberIds = latestService.studentMemberIds || [];
      if (memberIds.length > 0) {
        const result = await typedPostData<{ memberIds: string[] }, IMember[]>(
          '/api/member/getMembersByIds',
          { memberIds }
        );

      const sortedStudents = result.sort((a, b) => {
        if (a.lastName === b.lastName) {return a.firstName.localeCompare(b.firstName);}
        return a.lastName.localeCompare(b.lastName);
      });
      
      setStudents(sortedStudents);
      setPage(1);
      } else {
        setStudents([]);
      }
    } catch (err) {
      console.error('Failed to fetch student details', err);
    }
  }, [settings.userId, typedPostData]);

  const refreshAll = async (serviceId: string) => {
    await fetchStudentDetails(serviceId); // 刷新本地学生列表
    await fetchBookings();                // 刷新全局 booking
    await fetchSettings();                // 刷新全局 setting
  };

  useEffect(() => {
    if (open) {
      fetchStudentDetails(service.serviceId);
    }
  }, [open]);

  useEffect(() => {
    const nameSearch = searchTerm.trim().toLowerCase();
    if (!nameSearch) {
      setFilteredStudents(students);
    } else {
      setFilteredStudents(
        students.filter(student =>`${student.lastName}${student.firstName}`.toLowerCase().includes(nameSearch))
      );
    }
  }, [searchTerm, students]);

  const handleDeleteStudent = async (memberId: string) => {
    if (!settings.userId || !service?.serviceId) return;

    try {
      await postData('/api/setting/removeStudentFromService', {
        userId: settings.userId,
        serviceId: service.serviceId,
        memberId,
      });
      await fetchBookings();             
      await fetchSettings();
      setStudents((prev) => prev.filter((s) => s.memberId !== memberId));
    } catch (err) {
      console.error('Failed to remove student:', err);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Service Detail</DialogTitle>
        <DialogContent dividers>
          {!service ? (
            <Typography>No service selected.</Typography>
          ) : (
          <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Course"
                value={service.name}
                fullWidth
                InputProps={{ readOnly: true }}
                variant="standard"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Type"
                value={service.type}
                fullWidth
                InputProps={{ readOnly: true }}
                variant="standard"
              />
            </Grid>
            {settings.profile.role === Role.Admin && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Price"
                    value={service.price ? `$${service.price.toFixed(2)}` : ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="standard"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Teacher"
                    value={`${service.teacherId?.firstName ?? ''} ${service.teacherId?.lastName ?? ''}`}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="standard"
                  />
                </Grid>
              </>
            )}
            {service.type !== ServiceType.VIP && service.groupSize && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Group Size"
                  value={service.groupSize}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="standard"
                />
              </Grid>
            )}
            </Grid>
          </>
          )}
        </DialogContent>

        {service?.type !== ServiceType.VIP && 
        <>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography fontWeight={500} mr={2}>
                  Student Detail ({students.length} / {service?.groupSize || '∞'})
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: 200 }}
              />
            </Box>
          </DialogTitle>

                
          <DialogContent dividers>
          {students.length === 0 ? (
            <Typography>No students enrolled.</Typography>
          ) : (
            <Box>
              {filteredStudents.slice((page - 1) * studentsPerPage, page * studentsPerPage).map((student) => (
                <Box key={student.memberId} display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography>{student.lastName} {student.firstName}</Typography>
                {isAdmin && (
                  <Box display="flex" gap={1}>
                    <Button size="small" onClick={async () => {
                      try {
                        const payment = await typedPostData<{ serviceId: string; memberId: string }, IPayment>(
                          '/api/payment/getStudentPayment',
                          { serviceId: service.serviceId, memberId: student.memberId }
                        );
                        setInitialData({
                          member: student,
                          payment,
                        });
                        setOpenEditDialog(true);
                      } catch (err) {
                        console.error('Failed to fetch payment info:', err);
                      }
                    }}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => {
                      setTargetStudentId(student.memberId);
                      setConfirmDeleteOpen(true);
                    }}>
                      Delete
                    </Button>
                  </Box>
                )}
            </Box>
              
                
              ))}
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={Math.ceil(filteredStudents.length / studentsPerPage)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </Box>
          )}
          </DialogContent>
        </>}

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {isAdmin && service?.type !== ServiceType.VIP && (
            <Button
              variant="contained"
              onClick={() => setOpenAddStudent(true)}
              disabled={service?.groupSize !== undefined && students.length >= service.groupSize}
            >
              {service?.groupSize !== undefined && students.length >= service.groupSize ? 'Full' : 'Add Student'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {service?.type !== ServiceType.VIP && (
        <AddStudentDialog
          open={openAddStudent}
          serviceId={service?.serviceId}
          mode="add"
          onClose={() => setOpenAddStudent(false)}
          onSubmit={async () => {
            if (service?.serviceId) {
              await refreshAll(service.serviceId);
            }
            setOpenAddStudent(false);
          }}
        />
      )}

      {service?.type !== ServiceType.VIP && (
        <AddStudentDialog
          open={openEditDialog}
          serviceId={service?.serviceId}
          mode="edit"
          initialData={InitialData}
          onClose={() => setOpenEditDialog(false)}
          onSubmit={async () => {
            if (service?.serviceId) {
              await refreshAll(service.serviceId);
            }
            setOpenEditDialog(false);
          }}
        />
      )}

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={async () => {
          if (targetStudentId) {
            await handleDeleteStudent(targetStudentId);
            if (service?.serviceId) {
              await refreshAll(service.serviceId);
            }
            setTargetStudentId(null);
          }
          setConfirmDeleteOpen(false);
        }}
        itemName="student"
      />
    </>
  );
};

export default ServiceDetailDialog;

